/**
 * Sanchay -- Anjanikaa's saved-items ("wishlist") system.
 *
 * There is no native Shopify wishlist, so this is a small client-side
 * store backed by localStorage, plus two custom elements that hydrate
 * from it: <wishlist-button> (the flame toggle used on product cards
 * and the product page) and <wishlist-count> (the header badge).
 *
 * Products are identified by HANDLE (not numeric ID), because the
 * handle is what the public `/products/{handle}.js` endpoint needs
 * to look a saved product back up on the Sanchay page.
 *
 * The display name "Sanchay" lives in locales/en.default.json under
 * anjanikaa.wishlist.* -- rename it there, not here, to relabel the
 * whole feature at once. This file only knows it as "the wishlist".
 */
(function () {
  var STORAGE_KEY = 'anjanikaa:sanchay';

  function readHandles() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      return true;
    } catch (error) {
      // Private browsing / storage disabled / quota exceeded: fail
      // quietly rather than breaking the page for the shopper.
      return false;
    }
  }

  var Wishlist = {
    getAll: function () {
      return readHandles();
    },
    has: function (handle) {
      return readHandles().indexOf(String(handle)) !== -1;
    },
    count: function () {
      return readHandles().length;
    },
    add: function (handle) {
      var handles = readHandles();
      handle = String(handle);
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        writeHandles(handles);
        Wishlist._notify(handles);
      }
      return true;
    },
    remove: function (handle) {
      var handles = readHandles();
      handle = String(handle);
      var next = handles.filter(function (existing) {
        return existing !== handle;
      });
      if (next.length !== handles.length) {
        writeHandles(next);
        Wishlist._notify(next);
      }
      return false;
    },
    toggle: function (handle) {
      return Wishlist.has(handle) ? Wishlist.remove(handle) : Wishlist.add(handle);
    },
    _notify: function (handles) {
      window.dispatchEvent(
        new CustomEvent('wishlist:change', {
          detail: { handles: handles, count: handles.length },
        })
      );
    },
  };

  window.Anjanikaa = window.Anjanikaa || {};
  window.Anjanikaa.Wishlist = Wishlist;

  var HEART_PRESSED_ATTR = 'aria-pressed';

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.toggleButton = this.querySelector('.wishlist-button__toggle');
      this.labelEl = this.querySelector('[data-wishlist-label]');
      if (!this.handle || !this.toggleButton) return;

      this.render(Wishlist.has(this.handle));
      this.toggleButton.addEventListener('click', this.onClick.bind(this));
      this._onChange = this.onWishlistChange.bind(this);
      window.addEventListener('wishlist:change', this._onChange);
    }

    disconnectedCallback() {
      if (this._onChange) {
        window.removeEventListener('wishlist:change', this._onChange);
      }
    }

    onClick(event) {
      event.preventDefault();
      Wishlist.toggle(this.handle);
      this.render(Wishlist.has(this.handle));
    }

    onWishlistChange(event) {
      var saved = event.detail.handles.indexOf(this.handle) !== -1;
      this.render(saved);
    }

    render(saved) {
      this.toggleButton.setAttribute(HEART_PRESSED_ATTR, saved ? 'true' : 'false');
      this.classList.toggle('wishlist-button--saved', saved);
      var label = saved
        ? this.dataset.removeLabel || 'Remove from Sanchay'
        : this.dataset.addLabel || 'Add to Sanchay';
      this.toggleButton.setAttribute('aria-label', label);
      // Round-3 item 14: when a visible text label is present (product
      // page), swap its text too -- a second, unmistakable confirmation
      // alongside the flame's own fill + glow change.
      if (this.labelEl) {
        this.labelEl.textContent = label;
      }
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.bubble = this.querySelector('[data-wishlist-count-bubble]');
      this.countEl = this.querySelector('[data-wishlist-count]');
      this.a11yEl = this.querySelector('[data-wishlist-count-a11y]');
      this.update(Wishlist.count());
      this._onChange = function (event) {
        this.update(event.detail.count);
      }.bind(this);
      window.addEventListener('wishlist:change', this._onChange);
    }

    disconnectedCallback() {
      if (this._onChange) {
        window.removeEventListener('wishlist:change', this._onChange);
      }
    }

    update(count) {
      if (!this.bubble) return;
      this.bubble.hidden = count === 0;
      if (this.countEl) {
        this.countEl.textContent = count < 100 ? String(count) : '';
      }
      if (this.a11yEl) {
        this.a11yEl.textContent =
          count === 1 ? '1 item in your Sanchay' : count + ' items in your Sanchay';
      }
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }
  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }
})();
